import { useState } from 'react';
import { Button, Countdown, Icon, OtpInput, Typography } from '@/primer/components';
import { OTP_STATE, pluralize, type OtpState } from '@/utils';
import { PageLayout } from '@/components/layout';
import { deriveOtpScreen, getOtpErrorMessage, usesRetryTimer } from './InviteOtp.utils';

export interface InviteOtpViewProps {
  state: OtpState;
  attemptsRemaining: number;
  resendExpiresAt: number;
  retryExpiresAt?: number;
  inputResetKey?: number;
  onComplete: (code: string) => void;
  onResend: () => void;
}

function LockIcon() {
  return (
    <div className="flex size-10 items-center justify-center rounded-full bg-interactive-quiet">
      <Icon name="lock" size="large" color="interactive" />
    </div>
  );
}

function ResendTimer({ expiresAt, onExpire }: { expiresAt: number; onExpire: () => void }) {
  return (
    <Typography variant="body" size="medium" color="body" as="p">
      You can request a new code in{' '}
      <Countdown expiresAt={expiresAt} format="seconds" emphasis="bold" onExpire={onExpire} />{' '}
      <Typography variant="body" size="medium" as="span" decoration={{ bold: true }}>
        sec
      </Typography>
    </Typography>
  );
}

export function InviteOtpView({
  state,
  attemptsRemaining,
  resendExpiresAt,
  retryExpiresAt = 0,
  inputResetKey,
  onComplete,
  onResend,
}: InviteOtpViewProps) {
  const [now, setNow] = useState(Date.now);
  const refreshClock = () => setNow(Date.now());

  const expiresAt = usesRetryTimer(state) ? retryExpiresAt : resendExpiresAt;
  const screen = deriveOtpScreen(state, expiresAt > now);
  const errorMessage = getOtpErrorMessage(state);
  const error = errorMessage ? (
    <>
      {errorMessage}
      {state === OTP_STATE.INCORRECT && (
        <>
          <br />
          <Typography
            variant="body"
            size="medium"
            color="error"
            as="span"
            decoration={{ bold: true }}
          >
            {attemptsRemaining}
          </Typography>{' '}
          {pluralize(attemptsRemaining, 'attempt')} remaining.
        </>
      )}
    </>
  ) : undefined;

  return (
    <PageLayout className="py-8" gridProps={{ className: 'px-5' }}>
      <div className="flex flex-col items-center gap-5 text-center">
        {screen.showLockIcon && <LockIcon />}

        <div
          className={
            screen.showLockIcon && state !== OTP_STATE.ATTEMPTS_EXHAUSTED
              ? 'flex flex-col gap-1'
              : 'flex flex-col gap-2'
          }
        >
          <Typography variant="header" size="h1" as="h1">
            {screen.heading}
          </Typography>
          <Typography variant="body" size="medium" color="body">
            {screen.description}
          </Typography>
        </div>

        {screen.showCodeInput && (
          <div className="w-full max-w-sm">
            <OtpInput
              onComplete={onComplete}
              disabled={screen.isCodeInputDisabled}
              isVerifying={screen.isVerifying}
              error={error}
              resetKey={inputResetKey}
            />
          </div>
        )}

        {!screen.isLockedOut && (
          <div className="flex flex-col items-center gap-4">
            {screen.showResendTimer && (
              <ResendTimer expiresAt={expiresAt} onExpire={refreshClock} />
            )}
            <Button
              variant="tertiary"
              size="medium"
              icon="send"
              disabled={!screen.canResend}
              onClick={onResend}
            >
              Send code
            </Button>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
